
import { defineComponent, ref } from "vue";
import AppFooter from "@/components/common/AppFooter.vue";
import ConfirmationSave from "@/components/ConfirmationSave.vue";
import ConfirmationTable from "@/components/ConfirmationTable.vue";
import messages from "@/config/messages";
import routesPath from "@/config/routesPath";
import helper from "@/utils/helper";
import { mapMutations, mapGetters } from "vuex";
import common from "@/config/common";
import * as apiClient from "@/apiclient/";
import eventBus from "@/utils/eventBus";
import benefitMapper from "@/utils/benefitMapper";
import iBenefit from "@/types/iBenefit";
import iCOBRABenefit from "@/types/iCOBRABenefit";

export default defineComponent({
  name: "ConfirmationView",
  components: {
    AppFooter,
    ConfirmationSave,
    ConfirmationTable,
  },
  data() {
    return {
      validationMsg: "",
      confirmationAlldata: [] as apiClient.ParticipantDetailDto,
      profileData: {} as apiClient.ProfileResponseDto,
      qualifyingBeneficiaryData: {} as apiClient.QualifyingBeneficiaryDto,
      dependentData: [] as apiClient.GetAllDependentDto[],
      documentsData: [] as apiClient.FileResponse[],
      amountTodayData: {} as apiClient.GetAmountDueTodayDto,
      monthlyPremiumData: {} as apiClient.GetMonthlyPremiumDto,
      benefitsData: [] as iCOBRABenefit[],
      allCountries: [] as apiClient.CountryResponseDto[],
      allStates: [] as apiClient.StateResponseDto[],
      getCountryName: "",
      getStateName: "",
      getMonthlyCountryName: "",
      getMonthlyStateName: "",
      showConfirmationModal: false,
      messages,
      reviewChecked: false,
      genderOptions: [] as apiClient.GenderDto[],
      countries: {} as any,
      paymentMethods: {} as any,
      accountTypes: {} as any,
      isStripeEnabled: false,
      isPaymentEnabled: false,
      allBenefitsWaived: false,
      enrollmentType: 0 as number,
      openEnrollmentUnbundledBenefits:
        [] as apiClient.OpenEnrollmentBenefitDto[],
      openEnrollmentBundles: [] as apiClient.OpenEnrollmentBenefitsBundleDto[],
      mappedBenefits: [] as iBenefit[],
      cardDetail: {} as apiClient.CardDetail,
    };
  },
  async created() {
    this.enrollmentType = this.getEnrollment.serviceType;
    eventBus.emit("toggle-enrollments-menu", false);
    await this.getStripeEnabled();
    await this.getGender();
    await this.getPaymentMethods();
    await this.getAccountTypes();
    await this.getAllData();
    await this.getLocations();
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
    if (
      sessionStorage.getItem(common.storageVariables.allBenefitsWaived) ===
      "true"
    ) {
      this.allBenefitsWaived = true;
    }
  },
  beforeUnmount() {
    // Remove the beforeunload event handler when the component is destroyed
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    helper.beforeRouterLeave(next);
  },
  computed: {
    ...mapGetters(["getFormDirty", "getEnrollment"]),
  },
  methods: {
    ...mapMutations(["setFormDirty"]),
    openConfirmationModal() {
      this.showConfirmationModal = true;
    },
    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },
    async submitHandler() {
      if (this.allBenefitsWaived || this.enrollmentType == 1) {
        this.openConfirmationModal();
      } else {
        this.setFormDirty(false);
        helper.setPageStep(common.pageStep.confirmation);
        this.$router.push({ name: routesPath.payments });
      }
    },
    async confirmEnrollment() {
      this.closeConfirmationModal();
      this.setFormDirty(false);
      helper.setPageStep(common.pageStep.confirmation);
      document.getElementById("pdfContentContainer")!.style.display = "block";
      const pdfBlob: Blob = await helper.printPDF(
        document.getElementById("pdfContent")!,
        false,
        true
      );
      document.getElementById("pdfContentContainer")!.style.display = "none";
      this.afterPdfCreate(pdfBlob);
    },
    async afterPdfCreate(pdfBlob: Blob) {
      const params = {
        File: pdfBlob,
        customerParticipantId:
          this.getEnrollment.customerParticipantId.toString(),
      };
      const result = await apiClient
        .confirmationController()
        .putApiConfirmation(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.setFormDirty(false);
        sessionStorage.setItem(
          common.storageVariables.applicationSubmitted,
          "yes"
        );
        sessionStorage.setItem(common.storageVariables.isSubmitted, "yes");
        this.$router.push({ name: routesPath.applicationSuccessful });
      }
    },
    changeReview() {
      this.setFormDirty(true);
    },
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    async getLocations() {
      let locations = await helper.getLocationDetails(
        this.profileData.countryId!,
        this.profileData.geoStateId!,
        this.monthlyPremiumData.billingCountryId!,
        this.monthlyPremiumData.billingGeoStateId!
      );
      this.getCountryName = locations.getCountryName;
      this.getMonthlyCountryName = locations.getMonthlyCountryName;
      this.getStateName = locations.getStateName;
      this.getMonthlyStateName = locations.getMonthlyStateName;
      this.countries = locations.countries;
    },
    async getPaymentMethods(): Promise<void> {
      const result = await apiClient.paymentController().getApiPaymentType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.value) {
        this.paymentMethods = result.data.value;
      }
    },
    async getAccountTypes(): Promise<void> {
      const result = await apiClient
        .paymentController()
        .getApiPaymentAccountType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.value) {
        this.accountTypes = result.data.value;
      }
    },
    async getAllData(): Promise<void> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };

      if (this.getEnrollment.serviceType == 0) {
        const result = await apiClient
          .confirmationController()
          .getApiConfirmation(params);
        if (!result || result.error) {
          if (result.error) {
            this.validationMsg = result.error + messages.errorMsg;
          }
          return;
        } else if (result.data) {
          this.confirmationAlldata = result.data;
          this.profileData = result.data.profile!;
          this.qualifyingBeneficiaryData = result.data.qualifyingBeneficiary!;
          this.dependentData = result.data.dependents!;
          this.documentsData = result.data.documents!;
          this.amountTodayData = result.data.getPaymentAmountDueToday!;
          this.monthlyPremiumData = result.data.getPaymentMonthlyPremium!;
          this.benefitsData = benefitMapper.mapCOBRAApiBenefitsToBenefits(
            result.data.benefits!
          );
          this.cardDetail = result.data.cardDetail!;
        }
      } else {
        const response = await apiClient
          .confirmationController()
          .getApiConfirmationOpenEnrollment({
            customerParticipantId: this.getEnrollment.customerParticipantId,
          });

        if (!response || response.error) {
          if (response.error) {
            this.validationMsg = response.error + messages.oopsError;
          }

          return;
        } else if (response.data) {
          this.profileData = response.data.profile!;
          (this.qualifyingBeneficiaryData =
            response.data.qualifyingBeneficiary!),
            (this.dependentData = response.data.dependents!);
          this.documentsData = response.data.documents!;
          this.openEnrollmentUnbundledBenefits =
            response.data.unbundledBenefits!.filter(
              (benefit) => benefit.isSelected
            );
          this.openEnrollmentBundles = response.data.bundles!.filter(
            (benefit) => benefit.isSelected
          );
        }
      }
    },
    async backButtonHanlder() {
      this.$router.push({ name: routesPath.documents });
    },
    async getGender(): Promise<void> {
      let genderData = await helper.getGenders();
      if (genderData.validationMsg.length > 0) {
        this.validationMsg = genderData.validationMsg;
      } else {
        this.genderOptions = genderData.genderOptions!;
      }
    },
    async getStripeEnabled() {
      let stripeData = await helper.getStripeEnabled();
      if (stripeData) {
        if (stripeData!.validationMsg.length > 0) {
          this.validationMsg = stripeData.validationMsg;
          this.isStripeEnabled = false;
        } else {
          this.isStripeEnabled = stripeData.isStripeEnabled!;
        }
      }
    },
  },
});
